import { useState } from 'react'
import usePersistedNavigation from './usePersistedNavigation'
import { apiPost } from '@/services/apiService'

interface FormSubmissionState {
  isLoading: boolean
  error: string | null
  success: boolean
}

interface UseFormSubmissionProps {
  onSuccess?: (data: any) => void
  onError?: (error: Error, data: any) => void
}

interface RequestCallbackData {
  formId: string | null
  firstName: string
  lastName: string
  phone: string
  email: string
  comments: string
  preferredCallTime: string
  dealerId: string
  isPersonal: boolean
}

interface ApiResponse<T> {
  success: boolean
  responseMessage: string
  data: T
}

export const useFormSubmission = ({ onSuccess, onError }: UseFormSubmissionProps = {}) => {
  const navigate = usePersistedNavigation()
  const [submissionState, setSubmissionState] = useState<FormSubmissionState>({
    isLoading: false,
    error: null,
    success: false
  })

  const handleFormSave = async <T>(
    data: any,
    nextPath?: string
  ) => {
    // We don't want loading to be true, the save works silently behind the scenes and
    // we handle both success and error cases
    setSubmissionState(prev => ({ ...prev, isLoading: false, error: null }))

    const apiEndpoint = 'Form/SaveForm'

    // If redirect is immediate, navigate now
    if (nextPath) {
      navigate(nextPath)
    }

    try {
      const result: ApiResponse<T> = await apiPost(apiEndpoint, data)

      if (!result.success) {
        throw new Error(result.responseMessage || 'Failed to submit form')
      }

      setSubmissionState(prev => ({ ...prev, success: true }))

      if (nextPath) {
        onSuccess?.(result)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setSubmissionState(prev => ({ ...prev, error: errorMessage }))
      onError?.(err instanceof Error ? err : new Error(errorMessage), data)
    } finally {
      setSubmissionState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const handleFormSubmit = async <T>(
    data: any,
    recaptchaToken: string
  ) => {
    setSubmissionState(prev => ({ ...prev, isLoading: true, error: null }))

    const apiEndpoint = 'Form/SubmitForm'

    if (recaptchaToken && data) {
        data.recaptchaToken = recaptchaToken
    }

    try {
      const result: ApiResponse<T> = await apiPost(apiEndpoint, data)

      if (!result.success) {
        throw new Error(result.responseMessage || 'Failed to submit form')
      }

      setSubmissionState(prev => ({ ...prev, success: true }))

      onSuccess?.(result)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setSubmissionState(prev => ({ ...prev, error: errorMessage }))
      onError?.(err instanceof Error ? err : new Error(errorMessage), data)
    } finally {
      setSubmissionState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const handleCallbackRequest = async (data: RequestCallbackData) => {
    setSubmissionState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const result: ApiResponse<RequestCallbackData> = await apiPost('Form/SubmitCallback', data)

      if (!result.success) {
        throw new Error(result.responseMessage || 'Failed to submit callback request')
      }

      setSubmissionState(prev => ({ ...prev, success: true }))
      onSuccess?.(data)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      setSubmissionState(prev => ({ ...prev, error: errorMessage }))
      onError?.(error instanceof Error ? error : new Error(errorMessage), data)
    } finally {
      setSubmissionState(prev => ({ ...prev, isLoading: false }))
    }
  }

  return {
    ...submissionState,
    handleFormSave,
    handleFormSubmit,
    handleCallbackRequest
  }
}